/**
 * The coach's saved formations over HTTP — `/api/formations`.
 *
 * Listing reads each stored formation through `readFormation`. One it cannot
 * read — a newer version, say — is skipped and reported, never deleted: it is
 * still on the server, and a newer app will show it.
 */

import { readFormation, type SavedFormation } from '../core/index.ts';
import type { FormationList, FormationStore } from './types.ts';

export class HttpFormationStore implements FormationStore {
  readonly #getToken: () => Promise<string | null>;
  readonly #fetch: typeof fetch;

  constructor(getToken: () => Promise<string | null>, fetchImpl: typeof fetch = (...args) => fetch(...args)) {
    this.#getToken = getToken;
    this.#fetch = fetchImpl;
  }

  async list(): Promise<FormationList> {
    const response = await this.#request('GET', '/api/formations');
    if (!response.ok) throw new Error(`Could not load saved formations (${response.status})`);
    return readAll(await response.json());
  }

  async save(formation: SavedFormation): Promise<void> {
    const response = await this.#request('PUT', `/api/formations/${encodeURIComponent(formation.id)}`, JSON.stringify(formation));
    if (!response.ok) throw new Error(`Could not save the formation (${response.status})`);
  }

  async remove(id: string): Promise<void> {
    const response = await this.#request('DELETE', `/api/formations/${encodeURIComponent(id)}`);
    if (!response.ok && response.status !== 404) throw new Error(`Could not delete the formation (${response.status})`);
  }

  async #request(method: 'GET' | 'PUT' | 'DELETE', path: string, body?: string): Promise<Response> {
    const token = await this.#getToken();
    if (!token) throw new Error('Not signed in');
    return this.#fetch(path, {
      method,
      headers: {
        authorization: `Bearer ${token}`,
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      },
      ...(body !== undefined ? { body } : {}),
    });
  }
}

/** Reads what the server returned, keeping every formation it can and counting the rest. */
export function readAll(stored: unknown): FormationList {
  const formations: SavedFormation[] = [];
  let unreadable = 0;
  for (const item of Array.isArray(stored) ? stored : []) {
    try {
      formations.push(readFormation(item));
    } catch {
      unreadable += 1;
    }
  }
  return { formations, unreadable };
}

/** For tests and for anything that must not hit the network. */
export class MemoryFormationStore implements FormationStore {
  readonly saved = new Map<string, SavedFormation>();

  async list(): Promise<FormationList> {
    return { formations: [...this.saved.values()], unreadable: 0 };
  }

  async save(formation: SavedFormation): Promise<void> {
    this.saved.set(formation.id, formation);
  }

  async remove(id: string): Promise<void> {
    this.saved.delete(id);
  }
}
